<template>
  <div class="partner-tab-sharing-level-cell">
    <BittsPopover
      v-if="variant && hasPopover"
      v-model:open="isPopoverOpen"
      trigger="hover"
      placement="bottomLeft"
      :mount-to-body="true"
      :mouse-enter-delay="0.1"
      overlay-class="partner-tab-sharing-level-cell__popover"
      class="partner-tab-sharing-level-cell__trigger"
    >
      <button
        type="button"
        :aria-label="ariaLabel"
        class="partner-tab-sharing-level-cell__button"
        @click="openPartnerSettings"
        @focus="openPopoverOnKeyboardFocus"
        @blur="isPopoverOpen = false"
      >
        <PartnerTabSharingLevelPill :variant="variant" />
      </button>
      <template #content>
        <PartnerTabSharingLevelPopover
          :populations="segmentPopulations"
          :side="params.side"
          :partner-org-id="params.data!.id"
        />
      </template>
    </BittsPopover>
    <PartnerTabSharingLevelPill v-else-if="variant" :variant="variant" />
    <span v-else class="text-sm text-neutral-text-placeholder">&ndash;</span>
  </div>
</template>

<script setup lang="ts">
import { BittsPopover } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { computed, ref } from 'vue';

import { useForwardCellFocus } from '@/composables/useForwardCellFocus';
import {
  GREENFIELD_SHARING,
  HIDDEN,
  OLAPS_ONLY,
  SHARING,
  type SharingType,
} from '@/constants/data_shares';
import { SEGMENTS_TO_LABEL_MAP } from '@/types/populations';

import { SHARING_LEVEL_VARIANT_LABEL } from '../sharingLevelVariantLabels';
import type {
  PopulationSharingLevel,
  SharingLevelCellParamsType,
  SharingLevelVariantType,
  SharingPartnerRow,
  SharingSideType,
} from '../types';

import PartnerTabSharingLevelPill from './PartnerTabSharingLevelPill.vue';
import PartnerTabSharingLevelPopover from './PartnerTabSharingLevelPopover.vue';

const { params } = defineProps<{
  params: ICellRendererParams<SharingPartnerRow> & SharingLevelCellParamsType;
}>();

const LEVEL_VARIANT: Record<SharingType, SharingLevelVariantType> = {
  [GREENFIELD_SHARING]: 'all',
  [SHARING]: 'overlaps',
  [OLAPS_ONLY]: 'counts',
  [HIDDEN]: 'hidden',
};

const POPULATIONS_KEY: Record<
  SharingSideType,
  'incomingPopulations' | 'outgoingPopulations'
> = {
  incoming: 'incomingPopulations',
  outgoing: 'outgoingPopulations',
};

/** Request and Pending get their own hover behaviour in PGA-537. */
const VARIANTS_WITH_POPOVER = new Set<SharingLevelVariantType>([
  'all',
  'overlaps',
  'counts',
  'hidden',
]);

const variant = computed<SharingLevelVariantType | null>(() => {
  const level = params.data?.[params.side][params.segmentKey];
  return level ? LEVEL_VARIANT[level] : null;
});

const segmentPopulations = computed<PopulationSharingLevel[]>(() =>
  Object.values(params.data?.[POPULATIONS_KEY[params.side]] ?? {}).filter(
    (population) => population.segment === params.segmentKey,
  ),
);

const SIDE_LABEL: Record<SharingSideType, string> = {
  incoming: 'shared by',
  outgoing: 'shared with',
};

const isPopoverOpen = ref(false);

useForwardCellFocus(params.eGridCell);

const ariaLabel = computed(() =>
  variant.value
    ? `${SEGMENTS_TO_LABEL_MAP[params.segmentKey]} ${SIDE_LABEL[params.side]} ${params.data?.partner.name}: ${SHARING_LEVEL_VARIANT_LABEL[variant.value]}. Open sharing settings`
    : undefined,
);

/**
 * The partner settings modal hands focus back to this button when it closes.
 * After a mouse click that would reopen the popover away from the pointer, so
 * only keyboard focus, which the browser marks focus-visible, opens it.
 */
function openPopoverOnKeyboardFocus(event: FocusEvent) {
  if ((event.target as HTMLElement).matches(':focus-visible')) {
    isPopoverOpen.value = true;
  }
}

function openPartnerSettings() {
  if (!params.data) return;
  isPopoverOpen.value = false;
  params.context?.openPartnerSettings?.(
    params.data.id,
    params.side,
    params.segmentKey,
  );
}

const hasPopover = computed(
  () =>
    !!variant.value &&
    VARIANTS_WITH_POPOVER.has(variant.value) &&
    !!segmentPopulations.value.length,
);
</script>

<style lang="pcss">
@reference "../../../../css/theme.css";
.partner-tab-sharing-level-cell {
  @apply flex items-center justify-center h-full w-full py-6;
}
.partner-tab-sharing-level-cell__trigger {
  @apply h-full w-full;
}
.partner-tab-sharing-level-cell__button {
  @apply h-full w-full rounded-4 cursor-pointer;
}
.partner-tab-sharing-level-cell__popover.ant-popover {
  @apply w-[300px];

  .ant-popover-inner {
    @apply shadow-overlay;
  }
}
</style>
